import { authHeaders } from './http';
import { isCurrentSession, sessionIdentity } from './offlineSession';
const BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';
const PREFIX = 'luma:playback-progress:v1:';
export interface PlaybackProgress { render_id: string; listener_id: string; position_seconds: number; completed: boolean; revision: number }
interface Saved { progress: PlaybackProgress; pending?: { position_seconds: number; completed: boolean; expected_revision: number; mutation_id: string } }
function key(user: string, household: string, listener: string, render: string) { return PREFIX + [user, household, listener, render].join(':'); }
function read(k: string): Saved | null { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch { return null; } }
function write(k: string, value: Saved) { try { localStorage.setItem(k, JSON.stringify(value)); } catch { /* Private browsing may reject local persistence. */ } }
const inFlight = new Map<string, Promise<PlaybackProgress | null>>();
export async function restoreProgress(household: string, listener: string, render: string): Promise<PlaybackProgress | null> {
  const identity = sessionIdentity();
  if (!identity) return null;
  const k = key(identity.userId, household, listener, render);
  if (inFlight.has(k)) return inFlight.get(k)!;
  const sync = async () => {
    let saved = read(k);
    const url = `${BASE}/renders/${encodeURIComponent(render)}/progress?listener_id=${encodeURIComponent(listener)}`;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 5000);
    try {
      if (navigator.onLine === false) return saved?.progress || null;
      const pending = saved?.pending;
      const response = await fetch(url, { method: pending ? 'PUT' : 'GET', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, ...(pending ? { body: JSON.stringify(pending) } : {}), cache: 'no-store', signal: controller.signal });
      if (!isCurrentSession(identity)) return null;
      if (response.status === 401 || response.status === 403 || response.status === 404) { localStorage.removeItem(k); return null; }
      const data = await response.json();
      const progress = response.status === 409 ? data.detail?.progress : response.ok ? data : null;
      if (!progress) return read(k)?.progress || null;
      saved = read(k);
      // A newer local sample taken during this request remains pending against
      // the newly acknowledged revision. Conflicts always prefer server state.
      const newer = response.status !== 409 && saved?.pending && saved.pending.mutation_id !== pending?.mutation_id;
      write(k, newer ? { progress: { ...progress, position_seconds: saved!.pending!.position_seconds, completed: saved!.pending!.completed }, pending: { ...saved!.pending!, expected_revision: progress.revision } } : { progress });
      return read(k)?.progress || progress;
    } catch { return isCurrentSession(identity) ? read(k)?.progress || null : null; }
    finally { window.clearTimeout(timeout); }
  };
  const promise = sync().finally(() => inFlight.delete(k));
  inFlight.set(k, promise);
  return promise;
}
export function saveProgress(household: string, listener: string, render: string, position: number, completed: boolean): void {
  const identity = sessionIdentity();
  if (!identity || !Number.isFinite(position) || position < 0) return;
  const k = key(identity.userId, household, listener, render);
  const saved = read(k);
  const revision = saved?.pending?.expected_revision ?? saved?.progress.revision ?? 0;
  const pending = { position_seconds: position, completed, expected_revision: revision, mutation_id: crypto.randomUUID() };
  write(k, { progress: { render_id: render, listener_id: listener, position_seconds: position, completed, revision }, pending });
  void restoreProgress(household, listener, render);
}
